import { Col } from "react-bootstrap";
import { Row } from "reactstrap";
import * as yup from "yup";
import { useMemo } from "react";
import { FormikProps } from "formik";
import { ClaimHelper, FormatHelper, StringHelper, ValidationHelper, } from "../../assets/utils/helpers";
import { Constants } from "../../assets/utils/lib/constants";
import { LookupDto } from "../../assets/utils/lib/lookupDto";
import Drawer from "../../assets/vfx/drawer/drawer";
import Form from "../../assets/vfx/form/form";
import { CommunityDto } from "../../services/dto";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../assets/utils/lib";
import { ViewProps } from "./props";
import { roles } from "../../assets/configuration";
import TagInput from "../common/TagInput/TagInput";

interface CommunityFromProps {
    data?: CommunityDto | undefined;
    show: boolean;
    lookups: ViewProps.CommunityLookupProps | undefined;
    isLoading: boolean;
    onHide: () => void;
    onSave: (values: CommunityDto) => DrawerFormSaveResult;
}

const baseValidationSchema = yup.object().shape({
    name: yup.string().required("Name is required."),
    //abbreviation: yup.string().required("Abbreviation is required."),
    executiveEmail: yup.string().email("Invalid email."),
    contactName: yup.string().required("Contact name is required."),
    contactEmail: yup.string().required("Contact email is required.").email("Invalid email."),
    contactPhone: ValidationHelper.yupValidatePhone().required("Contact phone is required."),
    addressLine1: yup.string().required("Address line1 is required."),
    city: yup.string().required("City is required."),
    stateId: yup.string().required("State is required."),
    zip: yup.string().required("Zip is required."),
    publishedEmail: yup.string().email("Invalid email."),
    publishedPhone: ValidationHelper.yupValidatePhone(),
});

export const CommunityForm = (props: CommunityFromProps) => {
    let community: CommunityDto | undefined = props.data;
    let title: string =
        StringHelper.whenNullOrEmpty(community?.id, Constants.emptyGuid) ===
            Constants.emptyGuid
            ? "New Community"
            : "Edit Community";

    const isSystemAdmin = useMemo(
        () => ClaimHelper.hasRole([roles.SystemAdmin]),
        []
    );

    const fieldLabelWidth = "180px";
    const contactPersonSectionTitle = `${isSystemAdmin ? 4 : 3}. Contact Person`;
    const contactInfoSectionTitle = `${isSystemAdmin ? 5 : 4}. Contact Information`;
    const publishingSectionTitle = `${isSystemAdmin ? 6 : 5}. Publishing & Branding`;

    const activationDateSchema = yup.object().shape({
        activationDate: isSystemAdmin
            ? yup
                .date()
                .typeError("Please enter a valid date.")
                .nullable()
            //.required("Activation date is required.")
            : yup.date().typeError("Please enter a valid date.").nullable(),
    });

    const validationSchema = useMemo(() => {
        return isSystemAdmin
            ? baseValidationSchema.concat(activationDateSchema)
            : baseValidationSchema;
    }, [isSystemAdmin]);

    const onSubmit = (
        data: CommunityDto,
        formatedTotalBudget?: string | undefined,
        formatedGeneralFund?: string | undefined
    ) => {
        const updatedData: CommunityDto = {
            ...data,
            totalBudget: FormatHelper.unFormatCurrencyValue(formatedTotalBudget),
            generalFund: FormatHelper.unFormatCurrencyValue(formatedGeneralFund),
        };
        return Promise.resolve(props.onSave(updatedData)).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };

    return (
        <Drawer.Form
            className="w-70"
            show={props.show}
            backdrop={true}
            placement="end"
            scroll={true}
            onHide={props.onHide}
            title={title}
            initialValues={
                community
                    ? {
                        ...community,
                        formatedTotalBudget: FormatHelper.formatCurrencyValue(
                            community.totalBudget?.toString(),
                            true
                        ),
                        formatedGeneralFund: FormatHelper.formatCurrencyValue(
                            community.generalFund?.toString(),
                            true
                        ),
                    }
                    : { isServiceRequestEnabled: false }
            }
            validationSchema={validationSchema}
            on_Submit={(v: any) => {
                return onSubmit(v, v.formatedTotalBudget, v.formatedGeneralFund);
            }}
        >
            {(formikProps: FormikProps<CommunityDto>) => {
                const stateId = (formikProps.values as any)?.stateId;

                const countyOptions = useMemo(() => {
                    const all = (props.lookups as any)?.county ?? [];
                    const filtered = all.filter((x: { parentId?: string | null }) => String(x.parentId ?? "") === String(stateId ?? ""));
                    return LookupDto.mapToOptions(filtered);
                }, [(props.lookups as any)?.county, stateId]);

                const handleStateChange = (val: string | undefined) => {
                    formikProps.setFieldValue("stateId", val);
                    formikProps.setFieldValue("countyId", Constants.emptyGuid);
                };

                return (
                    <Row>
                        <Col xs={12}>
                            <Form.PanelSection iconClassName="bi bi-info-circle" title="1. Basic Information">
                                <Row>
                                    <Col xs={12} md={12}>
                                        <Form.Text
                                            name="name"
                                            placeholder="Enter text"
                                            label="Name"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                    <Col xs={12} md={12}>
                                        <Form.Text
                                            name="abbreviation"
                                            placeholder="Enter abbreviation"
                                            label="Abbreviation"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                    <Col xs={12} md={12}>
                                        <Form.Group label="Alias" labelWidth={fieldLabelWidth}>
                                            <TagInput
                                                name="alias"
                                                itemLabel="Alias"
                                                helperText="Press Enter or Return to add alias"
                                                placeholder="Add alias..."
                                            />
                                        </Form.Group>
                                    </Col>
                                    {isSystemAdmin && (
                                        <Col xs={12} md={12}>
                                            <Form.Date
                                                name="activationDate"
                                                label="Activation Date"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                                placeholder="Enter date"
                                            />
                                        </Col>
                                    )}
                                    <Col xs={12} md={12}>
                                        <Form.TextArea
                                            name="description"
                                            rows={3}
                                            label="Description"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                </Row>
                            </Form.PanelSection>
                        </Col>
                        <Col xs={12}>
                            <Form.PanelSection
                                iconClassName="bi bi-person-badge"
                                title="2. Executive"
                                style={{ marginTop: 16 }}
                            >
                                <Row>
                                    <Col xs={12} md={6}>
                                        <Form.Text
                                            name="executiveName"
                                            placeholder="Executive Name"
                                            label="Executive Name"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Text
                                            name="executiveTitle"
                                            placeholder="Executive Title"
                                            label="Executive Title"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Text
                                            name="executiveEmail"
                                            placeholder="Executive Email"
                                            label="Executive Email"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                    {isSystemAdmin && (
                                        <Col xs={12} md={6}>
                                            <Form.Text
                                                name="executiveRace"
                                                placeholder="Executive Race"
                                                label="Executive Race"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Form.PanelSection>
                        </Col>
                        {isSystemAdmin && (
                            <Col xs={12}>
                                <Form.PanelSection
                                    iconClassName="bi bi-graph-up"
                                    title="3. Overview"
                                    style={{ marginTop: 16 }}
                                >
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Form.Currency
                                                name="formatedTotalBudget"
                                                placeholder="Total Budget"
                                                label="Total Budget"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Currency
                                                name="formatedGeneralFund"
                                                placeholder="General Fund"
                                                label="General Fund"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Number
                                                name="population"
                                                placeholder="Population"
                                                label="Population"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Date
                                                name="nextElectionDate"
                                                label="Next Election Date"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                                placeholder="Enter date"
                                            />
                                        </Col>
                                    </Row>
                                </Form.PanelSection>
                            </Col>
                        )}
                        <Col xs={12}>
                            <Form.PanelSection
                                iconClassName="bi bi-person-lines-fill"
                                title={contactPersonSectionTitle}
                                style={{ marginTop: 16 }}
                            >
                                <Row className="gx-4 gy-3">
                                    <Col xs={12}>
                                        <Form.Text name="contactName" placeholder="Name" label="Name" labelWidth={fieldLabelWidth}  isLoading={props.isLoading} required />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Text name="contactJobTitle" placeholder="Job title" label="Job title" labelWidth={fieldLabelWidth} isLoading={props.isLoading} />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Phone name="contactPhone" placeholder="Phone" label="Phone" labelWidth={fieldLabelWidth} isLoading={props.isLoading} required />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Text name="contactEmail" placeholder="Email" label="Email" labelWidth={fieldLabelWidth} isLoading={props.isLoading} required />
                                    </Col>
                                </Row>
                            </Form.PanelSection>
                        </Col>
                        <Col xs={12}>
                            <Form.PanelSection iconClassName="bi bi-geo-alt" title={contactInfoSectionTitle} style={{ marginTop: 16 }} > 
                                <Form.Group label="Address" labelWidth={fieldLabelWidth} required>
                                    <Row className="gx-4 gy-2">
                                        <Col xs={12}>
                                            <Form.Text name="addressLine1" placeholder="Address line 1" isLoading={props.isLoading} />
                                        </Col>
                                        <Col xs={12}>
                                            <Form.Text name="addressLine2" placeholder="Address line 2" isLoading={props.isLoading} />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Text name="city" placeholder="City" isLoading={props.isLoading} />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Dropdown
                                                name="stateId"
                                                blankOption={{
                                                    key: Constants.emptyGuid,
                                                    display: "--Select state--",
                                                    value: Constants.emptyGuid,
                                                }}
                                                options={LookupDto.mapToOptions((props.lookups as any)?.state, undefined, { labelKey: "code" })}
                                                onChange={(e: any) => handleStateChange(e?.target?.value)}
                                                isLoading={props.isLoading}
                                            />
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Zip name="zip" placeholder="Zip" isLoading={props.isLoading} />
                                        </Col>
                                    </Row>
                                </Form.Group>

                                <Row className="gx-4 gy-3">
                                    <Col xs={12}>
                                        <Form.Group label="County" labelWidth={fieldLabelWidth}>
                                            <Form.Dropdown
                                                name="countyId"
                                                disabled={(formikProps.values as any).stateId === Constants.emptyGuid || (formikProps.values as any).stateId === undefined}
                                                isLoading={props.isLoading}
                                                blankOption={{
                                                    key: Constants.emptyGuid,
                                                    display: "--Select county--",
                                                    value: Constants.emptyGuid,
                                                }}
                                                options={countyOptions}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={12}>
                                        <Form.Phone
                                            name="publishedPhone"
                                            placeholder="Phone"
                                            label="Published phone"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Text
                                            name="publishedEmail"
                                            placeholder="Email"
                                            label="Published email"
                                            labelWidth={fieldLabelWidth}
                                            isLoading={props.isLoading}
                                        />
                                    </Col>
                                </Row>
                            </Form.PanelSection>
                        </Col>
                        <Col xs={12}>
                            <Form.PanelSection
                                iconClassName="bi bi-image"
                                title={publishingSectionTitle}
                                style={{ marginTop: 16 }}
                            >
                                {isSystemAdmin && (
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Form.Date
                                                name="hereNowPublishDate"
                                                label="Publish Date"
                                                labelWidth={fieldLabelWidth}
                                                isLoading={props.isLoading}
                                                placeholder="Enter date"
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Checkbox
                                                name="isServiceRequestEnabled"
                                                label="Enable Service Request"
                                            />
                                        </Col>
                                    </Row>
                                )}
                                <Form.Group
                                    label="Logo"
                                    labelWidth={fieldLabelWidth}
                                    note="Recommended Size 125 x 125"
                                    isLoading={props.isLoading}
                                >
                                    <Form.ImageCrop
                                        name="logo"
                                        loading={props.isLoading}
                                        aspect={1}
                                        maxOutputWidth={125}
                                        previewMaxHeight={125}
                                        modalTitle="Logo Crop"
                                        chooseText="Choose Logo"
                                        changeText="Change Logo"
                                    />
                                </Form.Group>
                            </Form.PanelSection>
                        </Col>
                    </Row>
                );
            }}
        </Drawer.Form>
    );
};